import * as React from 'react';
import { STATE } from 'app/constants';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { serializeAccessData } from 'app/area/administration/edit/serialize';
import { accessDataValidation } from 'app/area/administration/edit/access-data-validation';
import { AdministrationRoleEditProps, SetDefaultRole } from 'app/area/administration/edit/role/types';
import { PersonActionType } from 'app/area/administration/shared/types';
import { RoleDataFactoryProps } from './types';
import { editRoleValidationConfig } from 'app/area/administration/edit/role/role-edit-configuration';

// eslint:disable:max-func-body-length
export const roleEditFactory = (WrappedComponent:
  React.ComponentClass<AdministrationRoleEditProps> | React.FC<AdministrationRoleEditProps>) => {
  return class extends React.Component<AdministrationRoleEditProps> {
    public static statePath: string = STATE.administrationRoleEdit;

    private addRole = (setDefaultRoleData: SetDefaultRole) => {
      const {
        params: { actionType } = {}, validate, serialize, addRole, validateCustomData, translation,
        accessData, extraInfoCodes, data: originalData, sharedState: { access: { adminPanel } }
      } = this.props;

      const data = {
        ...serialize(defaultSerializeMappings),
        ...serializeAccessData(accessData, actionType, extraInfoCodes, originalData, adminPanel)
      };

      const accessDataErrors = accessDataValidation(accessData, translation.operation_required_message);

      accessDataErrors
        ? validateCustomData(accessDataErrors)
        : validate(editRoleValidationConfig, () => addRole(data, setDefaultRoleData), translation.validation_error);
    };

    private performUpdateRole = (setDefaultRoleData: SetDefaultRole) => {
      const {
        params: { id, actionType } = {}, validate, serialize, updateRole, validateCustomData, translation,
        accessData, extraInfoCodes, data: originalData, sharedState: { access: { adminPanel } }
      } = this.props;

      const data = {
        ...serialize(defaultSerializeMappings),
        ...serializeAccessData(accessData, actionType, extraInfoCodes, originalData, adminPanel)
      };

      const accessDataErrors = accessDataValidation(accessData, translation.operation_required_message);

      accessDataErrors
        ? validateCustomData(accessDataErrors)
        : validate(editRoleValidationConfig, () => updateRole(id, data, setDefaultRoleData), translation.validation_error);
    };

    private duplicateRole = (setDefaultRoleData: SetDefaultRole) => {
      const {
        params: { actionType } = {}, validate, serialize, duplicateRole, validateCustomData, translation = {},
        accessData, extraInfoCodes, data: originalData, sharedState: { access: { adminPanel } }
      } = this.props;

      const data = {
        ...serialize(defaultSerializeMappings),
        ...serializeAccessData(accessData, actionType, extraInfoCodes, originalData, adminPanel)
      };

      const accessDataErrors = accessDataValidation(accessData, translation.operation_required_message);

      accessDataErrors
        ? validateCustomData(accessDataErrors)
        : validate(editRoleValidationConfig, () => duplicateRole(data, setDefaultRoleData), translation.validation_error);
    };

    private create = (): AdministrationRoleEditProps => {
      const { id, loadRole, data, translation = {} } = this.props;

      const conf: { [key: string]: RoleDataFactoryProps } = {
        [PersonActionType.add]: {
          title: translation.new_role,
          saveButtonText: translation.edit_role_save_role,
          showEditMenu: false,
          showRoleId: false,
          onSave: this.addRole
        },

        [PersonActionType.edit]: {
          title: data && data.personId,
          saveButtonText: translation.edit_role_update_role,
          showEditMenu: true,
          showRoleId: !!id || id === 0,
          onSave: this.performUpdateRole,
          onLoadRole: () => loadRole(id)
        },

        [PersonActionType.duplicate]: {
          title: translation.edit_role_duplicate_role,
          saveButtonText: translation.edit_role_duplicate_role,
          showEditMenu: false,
          showRoleId: false,
          hideTopPanel: true,
          onSave: this.duplicateRole,
          onLoadRole: () => loadRole(id, { personId: '', name: '' })
        }
      };

      return { ...this.props, ...conf[this.props.params.actionType] } as AdministrationRoleEditProps;
    };

    public render() {
      return <WrappedComponent key={this.props.params.actionType} {...this.create()} />;
    }
  };
};
